import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AuroraItemReviewModal } from './AuroraItemReviewModal';
import { AuroraMappingModal } from './AuroraMappingModal';
import { AuroraPanelRowsReviewModal } from './AuroraPanelRowsReviewModal';
import { AuroraStructuresModal } from './AuroraStructuresModal';
import { ConfirmationModal, ConfirmationType } from './ConfirmationModal';
import {
  BomMatchResults,
  ReviewedItemSelections,
  AuroraSystemConfig,
  StructureRoofDetail,
  AuroraPanelPoolRow,
  extractAuroraPanelRowsPool,
} from '../../../utils/aurora-mapper.utils';
import {
  ModuleBrand,
  SolarModule,
  InverterBrand,
  Inverter,
  Optimizer,
} from '../../../types';
import { WorkflowStep, WORKFLOW_STEPS, TOTAL_WORKFLOW_STEPS } from '../../../constants';

interface AuroraWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Step 1 data
  bomMatchResults: BomMatchResults;
  moduleBrands: ModuleBrand[];
  solarModules: SolarModule[];
  inverterBrands: InverterBrand[];
  inverters: Inverter[];
  optimizers: Optimizer[];
  onReviewConfirm: (reviewed: ReviewedItemSelections, removedIds: Set<string>) => void;
  // Step 2 data
  systemConfig: AuroraSystemConfig;
  onMappingConfirm: (config: AuroraSystemConfig) => void;
  // Step 3 data
  initialStructures: StructureRoofDetail[];
  onStructuresChange?: (details: StructureRoofDetail[]) => void;
  // Design data for pool extraction
  designSummary: Record<string, unknown> | null;
  // Fires when the pool is first extracted (Step 3 confirm) — keeps Redux in sync early
  onPoolExtracted?: (pool: AuroraPanelPoolRow[]) => void;
  // Final completion: structures + user-reviewed panel rows pool
  onComplete: (details: StructureRoofDetail[], pool: AuroraPanelPoolRow[]) => void;
  // Preview mode: all steps pre-confirmed
  allConfirmed?: boolean;
  // Pre-existing pool for preview mode (from Redux)
  initialPool?: AuroraPanelPoolRow[];
}

export const AuroraWorkflowModal: React.FC<AuroraWorkflowModalProps> = ({
  isOpen,
  onClose,
  bomMatchResults,
  moduleBrands,
  solarModules,
  inverterBrands,
  inverters,
  optimizers,
  onReviewConfirm,
  systemConfig,
  onMappingConfirm,
  initialStructures,
  onStructuresChange,
  designSummary,
  onPoolExtracted,
  onComplete,
  allConfirmed = false,
  initialPool,
}) => {
  // Deferred state: structures confirmed in Step 3, pool extracted for Step 4 review
  const [confirmedStructures, setConfirmedStructures] = useState<StructureRoofDetail[]>(
    () => allConfirmed ? initialStructures : [],
  );
  const [extractedPool, setExtractedPool] = useState<AuroraPanelPoolRow[]>(
    () => {
      if (!allConfirmed) return [];
      // Use existing pool if available and has valid structure_index
      if (initialPool && initialPool.length > 0 && initialPool[0].structure_index != null) {
        return initialPool;
      }
      // Re-extract from design data when pool is missing or has stale format
      if (designSummary && initialStructures.length > 0) {
        return extractAuroraPanelRowsPool(designSummary, initialStructures);
      }
      return [];
    },
  );

  // Only pre-confirm Step 4 in preview mode if pool has rows
  const [activeStep, setActiveStep] = useState<WorkflowStep>(() => {
    if (!allConfirmed) return 1;
    return extractedPool.length > 0 ? 1 : 4;
  });
  const [confirmedSteps, setConfirmedSteps] = useState<Set<WorkflowStep>>(
    () => {
      if (!allConfirmed) return new Set<WorkflowStep>();
      const steps = new Set<WorkflowStep>([1, 2, 3] as WorkflowStep[]);
      if (extractedPool.length > 0) steps.add(4);
      return steps;
    },
  );
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  /** Intercept close — warn if not all steps are confirmed */
  const handleClose = useCallback(() => {
    if (confirmedSteps.size < TOTAL_WORKFLOW_STEPS) {
      setShowCloseWarning(true);
      return;
    }
    onClose();
  }, [confirmedSteps.size, onClose]);

  const handleStepClick = useCallback((step: WorkflowStep) => {
    const allDone = confirmedSteps.size === TOTAL_WORKFLOW_STEPS;
    if (allDone) {
      setActiveStep(step);
      return;
    }
    if (confirmedSteps.has(step) || step === activeStep) {
      setActiveStep(step);
    }
  }, [confirmedSteps, activeStep]);

  // Step 1 confirm → mark confirmed, advance to Step 2
  const handleReviewConfirm = useCallback((reviewed: ReviewedItemSelections, removedIds: Set<string>) => {
    onReviewConfirm(reviewed, removedIds);
    setConfirmedSteps(prev => { const next = new Set<WorkflowStep>(prev); next.add(1); return next; });
    setActiveStep(2);
  }, [onReviewConfirm]);

  // Step 2 confirm → mark confirmed, advance to Step 3
  const handleMappingConfirm = useCallback((config: AuroraSystemConfig) => {
    onMappingConfirm(config);
    setConfirmedSteps(prev => { const next = new Set<WorkflowStep>(prev); next.add(2); return next; });
    setActiveStep(3);
  }, [onMappingConfirm]);

  // Step 3 confirm → extract pool, save structures, advance to Step 4
  const handleStructuresComplete = useCallback((details: StructureRoofDetail[]) => {
    onStructuresChange?.(details);
    setConfirmedSteps(prev => { const next = new Set<WorkflowStep>(prev); next.add(3); return next; });
    setConfirmedStructures(details);

    // Extract panel rows pool from Aurora arrays for Step 4 review
    const pool = extractAuroraPanelRowsPool(designSummary, details);
    setExtractedPool(pool);
    onPoolExtracted?.(pool);
    setActiveStep(4);
  }, [designSummary, onStructuresChange, onPoolExtracted]);

  // Step 4 confirm → complete workflow with user-reviewed pool
  const handlePanelRowsConfirm = useCallback((pool: AuroraPanelPoolRow[]) => {
    setConfirmedSteps(prev => { const next = new Set<WorkflowStep>(prev); next.add(4); return next; });
    onComplete(confirmedStructures, pool);
  }, [confirmedStructures, onComplete]);

  if (!isOpen) return null;

  const allDone = confirmedSteps.size === TOTAL_WORKFLOW_STEPS;

  const getStepState = (step: WorkflowStep): 'active' | 'confirmed' | 'disabled' => {
    if (step === activeStep) return 'active';
    if (confirmedSteps.has(step)) return 'confirmed';
    return 'disabled';
  };

  const isStepClickable = (step: WorkflowStep): boolean => {
    if (allDone) return true;
    if (step === activeStep) return false;
    return confirmedSteps.has(step);
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl 3xl:max-w-7xl mx-auto shadow-2xl border border-gray-100 overflow-hidden flex flex-col h-[85vh]">
        {/* Unified step indicator */}
        <div className="px-5 pt-3 pb-2.5 3xl:px-6 3xl:pt-4 3xl:pb-3 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-center gap-2 3xl:gap-3">
            {WORKFLOW_STEPS.map(({ step, label }, idx) => {
              const state = getStepState(step);
              const clickable = isStepClickable(step);

              const indicator = (
                <div className="flex flex-col items-center gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] 3xl:text-[11px] font-medium uppercase tracking-wider text-slate-300">
                      Step {step}
                    </span>
                    {state === 'confirmed' ? (
                      <span className="w-4 h-4 3xl:w-[18px] 3xl:h-[18px] rounded-full bg-emerald-500 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 3xl:w-3 3xl:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    ) : (
                      <span className="w-4 h-4 3xl:w-[18px] 3xl:h-[18px] rounded-full bg-slate-200 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 3xl:w-3 3xl:h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    )}
                  </div>
                  <div className={`px-4 py-2 3xl:px-5 3xl:py-2.5 rounded-lg transition-colors flex items-center justify-center ${
                    state === 'active'
                      ? 'bg-blue-600 text-white'
                      : state === 'confirmed'
                        ? clickable
                          ? 'bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 cursor-pointer'
                          : 'bg-emerald-50 border border-emerald-200'
                        : 'bg-slate-100 text-slate-400'
                  }`}>
                    <span className={`text-[11px] 3xl:text-xs font-semibold leading-none ${
                      state === 'confirmed' ? 'text-emerald-700' : ''
                    }`}>
                      {label}
                    </span>
                  </div>
                </div>
              );

              return (
                <React.Fragment key={step}>
                  {idx > 0 && <div className="w-6 h-px bg-slate-200 mt-4" />}
                  {clickable ? (
                    <button onClick={() => handleStepClick(step)}>
                      {indicator}
                    </button>
                  ) : (
                    <div>{indicator}</div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step content — all steps stay mounted to preserve state, hidden via CSS */}
        <div className={activeStep === 1 ? 'contents' : 'hidden'}>
          <AuroraItemReviewModal
            embedded
            isOpen
            onClose={handleClose}
            onConfirm={handleReviewConfirm}
            matchResults={bomMatchResults}
            moduleBrands={moduleBrands}
            solarModules={solarModules}
            inverterBrands={inverterBrands}
            inverters={inverters}
            optimizers={optimizers}
          />
        </div>

        <div className={activeStep === 2 ? 'contents' : 'hidden'}>
          <AuroraMappingModal
            embedded
            isOpen
            onClose={handleClose}
            onConfirm={handleMappingConfirm}
            initialConfig={systemConfig}
          />
        </div>

        <div className={activeStep === 3 ? 'contents' : 'hidden'}>
          <AuroraStructuresModal
            embedded
            isOpen
            onClose={handleClose}
            onConfirm={handleStructuresComplete}
            onChange={onStructuresChange}
            initialDetails={initialStructures}
          />
        </div>

        <div className={activeStep === 4 ? 'contents' : 'hidden'}>
          <AuroraPanelRowsReviewModal
            embedded
            isOpen
            onClose={handleClose}
            onConfirm={handlePanelRowsConfirm}
            initialPool={extractedPool}
            structures={confirmedStructures}
          />
        </div>
      </div>

      {/* Warning when closing with incomplete steps */}
      <ConfirmationModal
        isOpen={showCloseWarning}
        onClose={() => setShowCloseWarning(false)}
        onConfirm={onClose}
        title="Leave Aurora Workflow?"
        message={`You have completed ${confirmedSteps.size} of ${TOTAL_WORKFLOW_STEPS} steps. Closing now will discard any unconfirmed progress. Are you sure you want to leave?`}
        type={ConfirmationType.WARNING}
        confirmButtonText="Leave"
        cancelButtonText="Stay"
      />
    </div>,
    document.body,
  );
};
